import { STATUS_FROM_API, STATUS_TO_API } from "@/features/tickets/config";
import {
  CreateCommentSchema,
  CreateReplySchema,
  CreateTicketSchema,
  DeleteCommentSchema,
  DeleteTicketSchema,
  EditTicketSchema,
  LikeCommentSchema,
  UpdateCommentSchema,
  createCommentSchema,
  createReplySchema,
  createTicketSchema,
  deleteCommentSchema,
  deleteTicketSchema,
  editTicketSchema,
  likeCommentSchema,
  updateCommentSchema,
} from "@/features/tickets/schemas";
import { Environment, Priority, Status, TicketType } from "@/features/tickets/types";
import { GetTicketCommentDto } from "@/features/tickets/types/dtos";
import { TicketFilters, TicketSort } from "@/features/tickets/types/filters";
import { Ticket, TicketComment, TicketCommentLikeStatus, TicketCommentsPage, TicketHistoryEntry, TicketListItem, TicketsPage } from "@/features/tickets/types/index";
import { CreateTicketCommentReplyRequest, CreateTicketCommentRequest, CreateTicketRequest, UpdateTicketCommentRequest, UpdateTicketRequest } from "@/features/tickets/types/requests";
import {
  CreateTicketCommentReplyResponse,
  CreateTicketCommentResponse,
  CreateTicketResponse,
  GetProjectTicketsResponse,
  GetTicketByIdResponse,
  GetTicketCommentsResponse,
  GetTicketHistoryResponse,
  LikeTicketCommentResponse,
  UpdateTicketCommentResponse,
} from "@/features/tickets/types/responses";
import { badRequest } from "@/shared/lib/utils/errors";
import http from "@/shared/lib/utils/http";
import { toErrorDetails } from "@/shared/lib/utils/zod";

export async function createTicket(values: CreateTicketSchema): Promise<string> {
  const validationResult = createTicketSchema.safeParse(values);

  if (!validationResult.success) {
    throw badRequest("Create Ticket", toErrorDetails(validationResult.error));
  }

  const request: CreateTicketRequest = {
    title: values.title.trim(),
    type: values.type,
    priority: values.priority,
    description: values.description?.trim(),
    assigned_to_id: values.assignedToId,
    tags: values.tags,
    effort: values.effort,
    reproducing_steps: values.reproducingSteps?.trim(),
    expected_behaviour: values.expectedBehaviour?.trim(),
    actual_behaviour: values.actualBehaviour?.trim(),
    affected_version: values.affectedVersion?.trim(),
    environment: values.environment,
  };

  const response = await http.post<CreateTicketResponse>({
    url: `/project/${values.projectId}/tickets`,
    body: JSON.stringify(request),
    contentType: "application/json",
  });

  return response.id;
}

export async function updateTicket(values: EditTicketSchema): Promise<void> {
  const validationResult = editTicketSchema.safeParse(values);

  if (!validationResult.success) {
    throw badRequest("Update Ticket", toErrorDetails(validationResult.error));
  }

  const request: UpdateTicketRequest = {
    title: values.title.trim(),
    status: STATUS_TO_API[values.status],
    type: values.type,
    priority: values.priority,
    description: values.description?.trim(),
    assigned_to_id: values.assignedToId ?? undefined,
    tags: values.tags,
    effort: values.effort,
    reproducing_steps: values.reproducingSteps?.trim(),
    expected_behaviour: values.expectedBehaviour?.trim(),
    actual_behaviour: values.actualBehaviour?.trim(),
    affected_version: values.affectedVersion?.trim(),
    environment: values.environment,
  };

  await http.put({
    url: `/project/${values.projectId}/tickets/${values.ticketId}`,
    body: JSON.stringify(request),
    contentType: "application/json",
  });
}

export async function getProjectTickets(projectId: string, startingAfter?: number | null, filters?: TicketFilters, sort?: TicketSort): Promise<TicketsPage> {
  const params = new URLSearchParams();

  if (startingAfter != null) {
    params.set("starting_after", String(startingAfter));
  }

  filters?.statuses.forEach((v) => params.append("statuses", v));
  filters?.priorities.forEach((v) => params.append("priorities", v));
  filters?.types.forEach((v) => params.append("types", v));
  filters?.assignees.forEach((v) => params.append("assignees", v));
  filters?.tags.forEach((v) => params.append("tags", v));

  if (sort?.sortBy) params.set("sort_by", sort.sortBy);
  if (sort?.sortDirection) params.set("sort_direction", sort.sortDirection);

  const response = await http.get<GetProjectTicketsResponse>({
    url: `/project/${projectId}/tickets?${params.toString()}`,
    contentType: "none",
  });

  const tickets = response.tickets.map(
    (dto): TicketListItem => ({
      id: dto.id,
      key: dto.key,
      title: dto.title,
      type: dto.type as TicketType,
      status: STATUS_FROM_API[dto.status] ?? (dto.status as Status),
      priority: dto.priority as Priority | null,
      assignee: dto.assignee ? { firstName: dto.assignee.first_name, lastName: dto.assignee.last_name } : null,
      createdBy: dto.created_by ? { firstName: dto.created_by.first_name, lastName: dto.created_by.last_name } : null,
      tags: dto.tags,
      commentCount: dto.comment_count,
      createdAt: new Date(dto.created_at),
      updatedAt: dto.updated_at ? new Date(dto.updated_at) : null,
    })
  );

  return {
    tickets,
    hasMore: response.has_more,
    nextStartingAfter: response.next_starting_after ?? null,
  };
}

export async function getTicketById(projectId: string, ticketId: string): Promise<Ticket> {
  const response = await http.get<GetTicketByIdResponse>({
    url: `/project/${projectId}/tickets/${ticketId}`,
    contentType: "none",
  });

  return {
    id: response.id,
    key: response.key,
    title: response.title,
    type: response.type as TicketType,
    status: STATUS_FROM_API[response.status] ?? (response.status as Status),
    priority: response.priority as Priority | null,
    description: response.description,
    assignee: response.assignee ? { memberId: response.assignee.member_id, firstName: response.assignee.first_name, lastName: response.assignee.last_name } : null,
    createdBy: response.created_by ? { firstName: response.created_by.first_name, lastName: response.created_by.last_name } : null,
    tags: response.tags,
    effort: response.effort,
    commentCount: response.comment_count,
    historyCount: response.history_count,
    reproducingSteps: response.reproducing_steps,
    expectedBehaviour: response.expected_behaviour,
    actualBehaviour: response.actual_behaviour,
    affectedVersion: response.affected_version,
    environment: response.environment as Environment | null,
    createdAt: new Date(response.created_at),
    updatedAt: response.updated_at ? new Date(response.updated_at) : null,
  };
}

export async function getTicketHistory(projectId: string, ticketId: string): Promise<TicketHistoryEntry[]> {
  const response = await http.get<GetTicketHistoryResponse>({
    url: `/project/${projectId}/tickets/${ticketId}/history`,
    contentType: "none",
  });

  return response.history.map(
    (dto): TicketHistoryEntry => ({
      id: dto.id,
      field: dto.field,
      oldValue: dto.old_value,
      newValue: dto.new_value,
      changedBy: { firstName: dto.changed_by.first_name, lastName: dto.changed_by.last_name },
      changedAt: new Date(dto.changed_at),
    })
  );
}

export async function deleteTicket(values: DeleteTicketSchema): Promise<undefined> {
  const validationResult = deleteTicketSchema.safeParse(values);

  if (!validationResult.success) {
    throw badRequest("Delete Ticket", toErrorDetails(validationResult.error));
  }

  await http.delete({
    url: `/project/${values.projectId}/tickets/${values.ticketId}`,
    contentType: "none",
  });
}

export async function createTicketComment(values: CreateCommentSchema): Promise<string> {
  const validationResult = createCommentSchema.safeParse(values);

  if (!validationResult.success) {
    throw badRequest("Create Comment", toErrorDetails(validationResult.error));
  }

  const request: CreateTicketCommentRequest = {
    body: values.body.trim(),
  };

  const response = await http.post<CreateTicketCommentResponse>({
    url: `/project/${values.projectId}/tickets/${values.ticketId}/comments`,
    body: JSON.stringify(request),
    contentType: "application/json",
  });

  return response.id;
}

export async function createTicketCommentReply(values: CreateReplySchema): Promise<string> {
  const validationResult = createReplySchema.safeParse(values);

  if (!validationResult.success) {
    throw badRequest("Create Reply", toErrorDetails(validationResult.error));
  }

  const request: CreateTicketCommentReplyRequest = {
    body: values.body.trim(),
  };

  const response = await http.post<CreateTicketCommentReplyResponse>({
    url: `/project/${values.projectId}/tickets/${values.ticketId}/comments/${values.commentId}/replies`,
    body: JSON.stringify(request),
    contentType: "application/json",
  });

  return response.id;
}

export async function updateTicketComment(values: UpdateCommentSchema): Promise<string> {
  const validationResult = updateCommentSchema.safeParse(values);

  if (!validationResult.success) {
    throw badRequest("Update Comment", toErrorDetails(validationResult.error));
  }

  const request: UpdateTicketCommentRequest = {
    body: values.body.trim(),
  };

  const response = await http.put<UpdateTicketCommentResponse>({
    url: `/project/${values.projectId}/tickets/${values.ticketId}/comments/${values.commentId}`,
    body: JSON.stringify(request),
    contentType: "application/json",
  });

  return response.id;
}

export async function deleteTicketComment(values: DeleteCommentSchema): Promise<undefined> {
  const validationResult = deleteCommentSchema.safeParse(values);

  if (!validationResult.success) {
    throw badRequest("Delete Comment", toErrorDetails(validationResult.error));
  }

  await http.delete({
    url: `/project/${values.projectId}/tickets/${values.ticketId}/comments/${values.commentId}`,
    contentType: "none",
  });
}

export async function likeTicketComment(values: LikeCommentSchema): Promise<TicketCommentLikeStatus> {
  const validationResult = likeCommentSchema.safeParse(values);

  if (!validationResult.success) {
    throw badRequest("Like Comment", toErrorDetails(validationResult.error));
  }

  const response = await http.post<LikeTicketCommentResponse>({
    url: `/project/${values.projectId}/tickets/${values.ticketId}/comments/${values.commentId}/like`,
    contentType: "none",
  });

  return {
    isLikedByMe: response.is_liked_by_me,
    likeCount: response.like_count,
  };
}

function mapCommentDto(dto: GetTicketCommentDto): TicketComment {
  return {
    id: dto.id,
    body: dto.body,
    author: { firstName: dto.author.first_name, lastName: dto.author.last_name },
    likeCount: dto.like_count,
    isLikedByMe: dto.is_liked_by_me,
    createdAt: new Date(dto.created_at),
    updatedAt: dto.updated_at ? new Date(dto.updated_at) : null,
    replies: dto.replies.map(mapCommentDto),
  };
}

export async function getTicketComments(projectId: string, ticketId: string, startingAfter?: number | null): Promise<TicketCommentsPage> {
  const params = new URLSearchParams();

  if (startingAfter != null) {
    params.set("starting_after", String(startingAfter));
  }

  const response = await http.get<GetTicketCommentsResponse>({
    url: `/project/${projectId}/tickets/${ticketId}/comments?${params.toString()}`,
    contentType: "none",
  });

  return {
    comments: response.comments.map(mapCommentDto),
    hasMore: response.has_more,
    nextStartingAfter: response.next_starting_after ?? null,
  };
}
